/**
 * Stock Adjustment Management Component
 * Display and manage stock adjustments with same pattern as CustomerList
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useInventory } from '../../../contexts/InventoryContext';
import inventoryApi from '../../../services/inventoryApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import PermissionGuard from '../../Common/PermissionGuard';
import ConfirmationModal from '../../Common/ConfirmationModal';
import StockAdjustmentForm from './StockAdjustmentForm';

const StockManagement = () => {
  const [showForm, setShowForm] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [viewMode, setViewMode] = useState(false); // true = view only, false = edit mode
  const { warehouses, items, fetchItems, fetchWarehouses } = useInventory();
  const [searchInput, setSearchInput] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [adjustmentTypeFilter, setAdjustmentTypeFilter] = useState('');
  const [errorState, setErrorState] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const searchTimeoutRef = useRef(null);
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
  const [pendingMovements, setPendingMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Stock adjustments from StockAdjustment table only
  const [stockAdjustments, setStockAdjustments] = useState([]);

  
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('stockAdjustmentView') || 'card';
    } catch {
      return 'card';
    }
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Initialize data on component mount
  useEffect(() => {
    initializeData();
  }, []);

  // Load all stock adjustments (not stock levels) on component mount
  useEffect(() => {
    loadAllStockAdjustments();
  }, []);

  const initializeData = async () => {
    try {
      // Load warehouses and items if not already loaded
      if (!warehouses?.length) {
        await fetchWarehouses();
      }
      if (!items?.length) {
        await fetchItems();
      }
      // Load stock adjustments
      await loadAllStockAdjustments();
    } catch (err) {
      console.error('Failed to initialize data:', err);
    }
  };

  const handleAddAdjustment = () => {
    setSelectedAdjustment(null);
    setViewMode(false);
    setShowForm(true);
  };

  const handleViewAdjustment = (adjustment) => {
    setSelectedAdjustment(adjustment);
    setViewMode(true); // Read-only mode
    setShowForm(true);
  };

  const handleEditAdjustment = (adjustment) => {
    setSelectedAdjustment(adjustment);
    setViewMode(false); // Edit mode
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedAdjustment(null);
    setViewMode(false);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedAdjustment(null);
    loadStockData();
  };

  const handleDeleteAdjustment = async (adjustment) => {
    setConfirmAction({
      type: 'danger',
      title: 'Delete Stock Adjustment',
      message: `Are you sure you want to delete this stock adjustment? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          // Mock delete - remove from state
          setStockAdjustments(prev => prev.filter(adj => adj._id !== adjustment._id));
          setConfirmAction(null); // Close modal
          loadStockData();
        } catch (err) {
          setConfirmAction(null); // Close modal even on error
          console.error('Delete stock adjustment error:', err);
        }
      }
    });
  };

  const loadAllStockAdjustments = async () => {
    try {
      setLoading(true);
      // Fetch ALL stock adjustments (pending, approved, rejected, cancelled)
      const response = await inventoryApi.getStockAdjustments();
      if (response.success && response.data) {
        // Transform the data
        const transformed = response.data.map((adj) => ({
          _id: adj._id,
          adjustmentNo: adj.adjustmentNo,
          item: adj.item || { name: 'Unknown Item', sku: 'N/A' },
          warehouse: adj.warehouse || { name: 'Unknown Warehouse', code: 'N/A' },
          adjustmentType: adj.adjustmentType,
          quantity: adj.quantityChange || adj.quantity || 0,
          unit: adj.unit || 'PCS',
          reason: adj.reason,
          reasonCode: adj.reasonCode,
          status: adj.status,
          createdAt: adj.createdAt || adj.submittedAt || new Date().toISOString(),
          location: adj.location,
          cost: {
            unitCost: adj.unitCost,
            totalCost: adj.totalCostImpact
          },
          isPending: adj.status === 'pending',
          submittedBy: adj.submittedBy,
          reviewedBy: adj.reviewedBy,
          beforeQuantity: adj.beforeQuantity,
          afterQuantity: adj.afterQuantity
        }));
        
        setStockAdjustments(transformed);
        
        // Update pending count for banner
        const pendingCount = transformed.filter(adj => adj.status === 'pending');
        setPendingMovements(pendingCount);
      }
      setErrorState('');
    } catch (err) {
      setErrorState(err.response?.data?.message || 'Failed to fetch stock adjustments');
      console.error('Fetch stock adjustments error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStockData = async () => {
    await loadAllStockAdjustments();
  };

  const handleApproveAdjustment = async (adjustment) => {
    setConfirmAction({
      type: 'success',
      title: 'Approve Stock Adjustment',
      message: `Are you sure you want to approve adjustment ${adjustment.adjustmentNo || adjustment._id}? This will update the stock levels.`,
      confirmText: 'Approve',
      onConfirm: async () => {
        try {
          await inventoryApi.approveStockAdjustmentById(adjustment._id, {
            notes: 'Approved via stock management'
          });
          setConfirmAction(null); // Close modal
          await loadStockData();
          setErrorState('');
        } catch (err) {
          setConfirmAction(null); // Close modal even on error
          setErrorState(err.response?.data?.message || 'Failed to approve adjustment');
          console.error('Approve adjustment error:', err);
        }
      }
    });
  };

  const handleRejectAdjustment = async (adjustment) => {
    setConfirmAction({
      type: 'danger',
      title: 'Reject Stock Adjustment',
      message: `Are you sure you want to reject adjustment ${adjustment.adjustmentNo || adjustment._id}? This action cannot be undone.`,
      confirmText: 'Reject',
      onConfirm: async () => {
        try {
          await inventoryApi.rejectStockAdjustmentById(adjustment._id, {
            reason: 'Rejected via stock management'
          });
          setConfirmAction(null); // Close modal
          await loadStockData();
          setErrorState('');
        } catch (err) {
          setConfirmAction(null); // Close modal even on error
          setErrorState(err.response?.data?.message || 'Failed to reject adjustment');
          console.error('Reject adjustment error:', err);
        }
      }
    });
  };

  const handleSearch = (value) => {
    setSearchInput(value);
  };

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('stockAdjustmentView', newView);
    } catch (error) {
      console.warn('Failed to save view preference:', error);
    }
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setWarehouseFilter('');
    setStatusFilter('');
    setAdjustmentTypeFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasFilters = () => searchInput || warehouseFilter || statusFilter || adjustmentTypeFilter;

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ page: 1, limit: newLimit, total: prev.total, pages: prev.pages }));
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      approved: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getAdjustmentTypeBadgeColor = (type) => {
    const colors = {
      increase: 'bg-green-100 text-green-800 border-green-200',
      decrease: 'bg-orange-100 text-orange-800 border-orange-200',
      set: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const filteredAdjustments = (stockAdjustments || []).filter(adjustment => {
    const matchesSearch = !debouncedSearchInput || 
      adjustment.item?.name?.toLowerCase().includes(debouncedSearchInput.toLowerCase()) ||
      adjustment.item?.sku?.toLowerCase().includes(debouncedSearchInput.toLowerCase()) ||
      adjustment.adjustmentNo?.toLowerCase().includes(debouncedSearchInput.toLowerCase()) ||
      adjustment.warehouse?.name?.toLowerCase().includes(debouncedSearchInput.toLowerCase()) ||
      adjustment.location?.toLowerCase().includes(debouncedSearchInput.toLowerCase());
    
    const matchesWarehouse = !warehouseFilter || adjustment.warehouse?._id === warehouseFilter;
    const matchesStatus = !statusFilter || adjustment.status === statusFilter;
    const matchesType = !adjustmentTypeFilter || adjustment.adjustmentType === adjustmentTypeFilter;
    
    return matchesSearch && matchesWarehouse && matchesStatus && matchesType;
  });

  // Calculate pagination for filtered data
  const totalFiltered = filteredAdjustments.length;
  const totalPages = Math.ceil(totalFiltered / pagination.limit);
  const startIndex = (pagination.page - 1) * pagination.limit;
  const endIndex = startIndex + pagination.limit;
  const paginatedAdjustments = filteredAdjustments.slice(startIndex, endIndex);

  // Update pagination info
  const currentPagination = {
    ...pagination,
    total: totalFiltered,
    pages: totalPages
  };

  const renderAdjustmentCard = (adjustment) => {
    const isPending = adjustment.status === 'pending';
    
    return (
      <div key={adjustment._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-32 w-full">
        <div className="p-3 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0 ${getAdjustmentTypeBadgeColor(adjustment.adjustmentType)}`}>
                {adjustment.adjustmentType === 'increase' ? '+' : adjustment.adjustmentType === 'decrease' ? '-' : '='}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-1">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {adjustment.item?.name || 'Unknown Item'}
                  </div>
                </div>
                <p className="text-xs text-gray-500 font-mono truncate">{adjustment.adjustmentNo || adjustment._id}</p>
              </div>
            </div>
            
            <div className="ml-2 flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              {/* View button - always visible */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleViewAdjustment(adjustment);
                }}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title="View adjustment"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>

              {/* Edit button - for non-finalized adjustments */}
              {adjustment.status !== 'approved' && adjustment.status !== 'rejected' && (
                <PermissionGuard permission="INVENTORY:STOCK:UPDATE">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditAdjustment(adjustment);
                    }}
                    className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                    title="Edit adjustment"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </PermissionGuard>
              )}
              
              {/* Delete button - for non-finalized adjustments */}
              {adjustment.status !== 'approved' && adjustment.status !== 'rejected' && (
                <PermissionGuard permission="INVENTORY:STOCK:DELETE">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteAdjustment(adjustment);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                    title="Delete adjustment"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </PermissionGuard>
              )}
              
              {/* Approve/Reject buttons - only for pending adjustments */}
              {isPending && (
                <>
                  <PermissionGuard permission="INVENTORY:STOCK:APPROVE">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleApproveAdjustment(adjustment);
                      }}
                      className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                      title="Approve adjustment"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  </PermissionGuard>
                  <PermissionGuard permission="INVENTORY:STOCK:APPROVE">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleRejectAdjustment(adjustment);
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                      title="Reject adjustment"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </PermissionGuard>
                </>
              )}
            </div>
          </div>

        {/* Details */}
        <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-tight flex-1">
          {adjustment.warehouse?.name && <span className="font-medium">🏪 {adjustment.warehouse.name}</span>}
          {adjustment.location && <span className="text-gray-500"> ({adjustment.location})</span>}
          {adjustment.quantity !== undefined && (
            <>
              <br />
              <span className="text-gray-700 font-medium">
                📊 Stock: {adjustment.quantity} {adjustment.unit || 'PCS'}
              </span>
              {adjustment.availableQuantity !== undefined && (
                <span className="text-green-600"> | Available: {adjustment.availableQuantity}</span>
              )}
            </>
          )}
          {adjustment.cost?.unitCost && (
            <>
              <br />
              <span className="text-gray-500">� Cost: ${adjustment.cost.unitCost.toFixed(2)}</span>
            </>
          )}
        </p>

        {/* Badges */}
        <div className="flex flex-wrap gap-1 mt-auto">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(adjustment.status)}`}>
            {adjustment.status?.toUpperCase()}
          </span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getAdjustmentTypeBadgeColor(adjustment.adjustmentType)}`}>
            {adjustment.adjustmentType}
          </span>
          {adjustment.reasonCode && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {adjustment.reasonCode}
            </span>
          )}
        </div>
      </div>
    </div>
    );
  };

  const renderAdjustmentRow = (adjustment) => {
    const isPending = adjustment.status === 'pending';
    
    return (
    <tr key={adjustment._id} className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 whitespace-nowrap">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {adjustment.item?.name || 'Unknown Item'}
          </div>
          <p className="text-xs text-gray-500 font-mono">{adjustment.adjustmentNo || adjustment._id}</p>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-sm text-gray-900">{adjustment.warehouse?.name || 'Unknown'}</div>
        <p className="text-xs text-gray-500">
          {adjustment.warehouse?.code || 'No Code'}
          {adjustment.location && ` • ${adjustment.location}`}
        </p>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        <div className="text-sm font-medium text-gray-900">
          {adjustment.quantity} {adjustment.unit || 'PCS'}
        </div>
        {adjustment.availableQuantity !== undefined && (
          <p className="text-xs text-green-600">Avail: {adjustment.availableQuantity}</p>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {adjustment.cost?.unitCost ? `$${adjustment.cost.unitCost.toFixed(2)}` : 'N/A'}
        </div>
        {adjustment.cost?.totalCost && (
          <p className="text-xs text-gray-500">Total: ${adjustment.cost.totalCost.toFixed(2)}</p>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusBadgeColor(adjustment.status)}`}>
          {adjustment.status}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          {/* View button - always visible */}
          <button
            onClick={(e) => {
              e.preventDefault();
              handleViewAdjustment(adjustment);
            }}
            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-all"
            title="View adjustment"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>

          {/* Edit button - for non-finalized adjustments */}
          {adjustment.status !== 'approved' && adjustment.status !== 'rejected' && (
            <PermissionGuard permission="INVENTORY:STOCK:UPDATE">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleEditAdjustment(adjustment);
                }}
                className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                title="Edit adjustment"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </PermissionGuard>
          )}
          
          {/* Delete button - for non-finalized adjustments */}
          {adjustment.status !== 'approved' && adjustment.status !== 'rejected' && (
            <PermissionGuard permission="INVENTORY:STOCK:DELETE">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteAdjustment(adjustment);
                }}
                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                title="Delete adjustment"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </PermissionGuard>
          )}
          
          {/* Approve/Reject buttons - only for pending adjustments */}
          {isPending && (
            <>
              <PermissionGuard permission="INVENTORY:STOCK:APPROVE">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleApproveAdjustment(adjustment);
                  }}
                  className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-all"
                  title="Approve adjustment"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </PermissionGuard>
              <PermissionGuard permission="INVENTORY:STOCK:APPROVE">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleRejectAdjustment(adjustment);
                  }}
                  className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                  title="Reject adjustment"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </PermissionGuard>
            </>
          )}
        </div>
      </td>
    </tr>
    );
  };

  if (loading && paginatedAdjustments.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-white shadow-sm border border-gray-200">
      {/* Error Display */}
      {errorState && (
        <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
          <div className="flex items-center">
            <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-red-800">{errorState}</p>
            <button
              onClick={() => setErrorState('')}
              className="ml-auto text-red-600 hover:text-red-500"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          {/* Search */}
          <div className="flex-1">
            <TextInput
              variant="input"
              type="text"
              placeholder="Search stock adjustments by item, warehouse, adjustment number..."
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              icon={({ className }) => (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
              className="text-sm py-1.5 border border-gray-300 shadow-sm !rounded-sm"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <CustomSelect
              value={warehouseFilter}
              onChange={(e) => {
                setWarehouseFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              options={[
                { value: '', label: 'All Warehouses' },
                ...((warehouses && Array.isArray(warehouses)) ? warehouses : []).map(warehouse => ({
                  value: warehouse._id,
                  label: warehouse.name
                }))
              ]}
              size="sm"
              variant="default"
              className="min-w-[120px] !rounded-sm"
              placeholder=""
            />

            <CustomSelect
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              options={[
                { value: '', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
              ]}
              size="sm"
              variant="default"
              className="min-w-[120px] !rounded-sm"
              placeholder=""
            />

            <CustomSelect
              value={adjustmentTypeFilter}
              onChange={(e) => {
                setAdjustmentTypeFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              options={[
                { value: '', label: 'All Types' },
                { value: 'increase', label: 'Increase' },
                { value: 'decrease', label: 'Decrease' },
                { value: 'set', label: 'Set Exact' }
              ]}
              size="sm"
              variant="default"
              className="min-w-[120px] !rounded-sm"
              placeholder=""
            />

            {hasFilters() && (
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                title="Reset filters"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
            )}

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
              <button
                onClick={() => handleViewChange('card')}
                className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                  view === 'card' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Cards
              </button>
              <button
                onClick={() => handleViewChange('list')}
                className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                  view === 'list' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                List
              </button>
            </div>

            {/* Add Stock Adjustment Button */}
            <PermissionGuard permission="INVENTORY:STOCK:CREATE">
              <button
                onClick={handleAddAdjustment}
                className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Stock Adjustment
              </button>
            </PermissionGuard>
          </div>
        </div>
      </div>

      {/* Pagination Section */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Results Info */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-sm flex items-center gap-2">
              <div>
                <span className="font-semibold text-gray-900">
                  {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>
                <span className="text-gray-500 mx-1">of</span>
                <span className="font-semibold text-gray-900">{pagination.total}</span>
                <span className="text-gray-500 ml-1">adjustments</span>
              </div>
              {loading && filteredAdjustments.length > 0 && (
                <div className="flex items-center">
                  <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center gap-3">
            {/* Items per page */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Show:</span>
              <CustomSelect
                value={pagination.limit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                disabled={loading}
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' }
                ]}
                size="sm"
                variant="minimal"
                className="min-w-[70px]"
              />
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPagination.page <= 1 || loading}
                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="First page"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={() => handlePageChange(Math.max(1, currentPagination.page - 1))}
                disabled={currentPagination.page <= 1 || loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Previous</span>
              </button>

              {/* Page Numbers */}
              <div className="hidden md:flex items-center gap-1">
                {Array.from({ length: Math.min(7, currentPagination.pages) }, (_, i) => {
                  const pageNum = i + 1;
                  const isCurrentPage = pageNum === currentPagination.page;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${
                        isCurrentPage
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {currentPagination.pages > 7 && (
                  <span className="px-2 text-gray-400 text-sm">...</span>
                )}
              </div>

              {/* Mobile page indicator */}
              <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                <span className="text-sm font-medium text-gray-900">{currentPagination.page}</span>
                <span className="text-sm text-gray-500 mx-1">of</span>
                <span className="text-sm font-medium text-gray-900">{currentPagination.pages}</span>
              </div>

              <button
                onClick={() => handlePageChange(Math.min(currentPagination.pages, currentPagination.page + 1))}
                disabled={currentPagination.page >= currentPagination.pages || loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
              >
                <span className="hidden sm:inline">Next</span>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => handlePageChange(currentPagination.pages)}
                disabled={currentPagination.page >= currentPagination.pages || loading}
                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Last page"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {view === 'card' ? (
        <div className="px-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {paginatedAdjustments.length === 0 ? (
              <div className="col-span-full bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-sm font-medium text-gray-900 mb-2">No stock adjustments found</h3>
                <p className="text-sm text-gray-500 mb-4">
                  {hasFilters() ? 'Try adjusting your search or filters' : 'Get started by creating your first stock adjustment'}
                </p>
                {!hasFilters() && (
                  <PermissionGuard permission="INVENTORY:STOCK:CREATE">
                    <button
                      onClick={handleAddAdjustment}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Stock Adjustment
                    </button>
                  </PermissionGuard>
                )}
              </div>
            ) : (
              paginatedAdjustments.map(renderAdjustmentCard)
            )}
          </div>
        </div>
      ) : (
        <div className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-max w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Warehouse</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Unit Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedAdjustments.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <h3 className="text-sm font-medium text-gray-900 mb-2">No stock levels found</h3>
                      <p className="text-sm text-gray-500">
                        {hasFilters() ? 'Try adjusting your search or filters' : 'No stock data available'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedAdjustments.map(renderAdjustmentRow)
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stock Adjustment Form Modal */}
      {showForm && (
        <StockAdjustmentForm
          stockAdjustment={selectedAdjustment}
          viewMode={viewMode}
          onSuccess={handleFormSuccess}
          onCancel={handleFormClose}
        />
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <ConfirmationModal
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          type={confirmAction.type}
        />
      )}
    </div>
  );
};

export default StockManagement;
